import React, { useMemo, useState } from 'react';

import Modal from '../modal/modal';

import Card from '../card/Card';

import P from '../texts/p/P';

import DatePicker from '../datepicker/DatePicker';

import EarlyCancellationBanner from './EarlyCancellationBanner';

import {

  formatOriginalDueDateDisplay,

  previewLoanEarlyCancellation,

  validateEarlyCancellationDate,

} from '../../utils/earlyCancellationUtils';



export type EarlyCancellationDateMode = 'loan' | 'installment' | 'instrument';



export interface EarlyCancellationDateModalProps {

  isOpen: boolean;

  onClose: () => void;

  onContinue: (date: string) => void;

  maxDueDate: string;

  mode: EarlyCancellationDateMode;

  avalNumber?: string | number | null;

  companyName?: string | null;

  operationType?: string | null;

  currentDueDate?: string | null;

  /** Loan: all installments for live preview of reached/total pending */

  loanInstallments?: Array<{ due_date?: string; status_type?: string }>;

  totalPendingCount?: number;

  installmentNumber?: number | null;

  totalInstallments?: number | null;

  installmentAmount?: number | string | null;

}



const SummaryRow: React.FC<{ label: string; value: React.ReactNode }> = ({

  label,

  value,

}) => (

  <Card margin={{ bottom: '2' }} display="flex" gap="4px" alignItems="baseline" flexWrap="wrap">

    <P type="3" variant="neutral" weight="bold" modifier="strong" withMargin={false}>

      {label}:

    </P>

    <P type="3" variant="neutral" weight="regular" modifier="regular" withMargin={false}>

      {value}

    </P>

  </Card>

);



const parseMaxDueDate = (maxDueDate: string): Date | undefined => {

  if (!maxDueDate) return undefined;

  const [year, month, day] = maxDueDate.split('T')[0].split('-').map(Number);

  if (!year || !month || !day) return undefined;

  return new Date(year, month - 1, day);

};



const EarlyCancellationDateModal: React.FC<EarlyCancellationDateModalProps> = ({

  isOpen,

  onClose,

  onContinue,

  maxDueDate,

  mode,

  avalNumber,

  companyName,

  operationType,

  currentDueDate,

  loanInstallments = [],

  totalPendingCount = 0,

  installmentNumber,

  totalInstallments,

  installmentAmount,

}) => {

  const [selectedDate, setSelectedDate] = useState<string>('');

  const [error, setError] = useState<string | null>(null);



  const title =

    mode === 'installment'

      ? `Cancelación anticipada - Cuota ${installmentNumber ?? '-'}/${totalInstallments ?? '-'}`

      : `Cancelación anticipada - Aval N° ${avalNumber ?? '-'}`;



  const loanReached = useMemo(() => {

    if (mode !== 'loan' || !selectedDate) return null;

    return previewLoanEarlyCancellation(loanInstallments, selectedDate).reached;

  }, [mode, selectedDate, loanInstallments]);



  const infoText = useMemo(() => {

    if (mode === 'loan') {

      return 'La fecha no puede ser posterior al vencimiento original de cada cuota. Las cuotas pendientes con vencimiento anterior a la fecha elegida no se modifican.';

    }

    if (mode === 'installment') {

      const maxLabel = formatOriginalDueDateDisplay(maxDueDate);

      return `Solo se modifica esta cuota. El resto del plan queda igual. La fecha no puede ser posterior al ${maxLabel}.`;

    }

    return 'La fecha no puede ser posterior al vencimiento actual.';

  }, [mode, maxDueDate]);



  const handlePrimary = () => {

    const validationError = validateEarlyCancellationDate(selectedDate, maxDueDate);

    if (validationError) {

      setError(validationError);

      return;

    }

    setError(null);

    onContinue(selectedDate);

  };



  const handleClose = () => {

    setSelectedDate('');

    setError(null);

    onClose();

  };



  const amountDisplay =

    installmentAmount === null || installmentAmount === undefined || installmentAmount === ''

      ? '-'

      : Number(installmentAmount).toLocaleString('es-AR', {

          minimumFractionDigits: 2,

          maximumFractionDigits: 2,

        });



  return (

    <Modal

      isOpen={isOpen}

      onClose={handleClose}

      title={title}

      onSecondaryAction={handleClose}

      secondaryVariant="secondary"

      secondaryText="Cancelar"

      onPrimaryAction={handlePrimary}

      primaryVariant="primary"

      primaryText="Confirmar cancelación"

      primaryActionDisabled={!selectedDate}

      width="560px"

      overflow="visible"

    >

      <Card margin={{ top: '3', bottom: '3' }}>

        {mode === 'installment' ? (

          <>

            <SummaryRow

              label="Aval"

              value={`N° ${avalNumber ?? '-'} - ${companyName || '-'}`}

            />

            <SummaryRow label="Total cuota" value={amountDisplay} />

            <SummaryRow

              label="Fecha de vencimiento actual"

              value={formatOriginalDueDateDisplay(currentDueDate || maxDueDate)}

            />

          </>

        ) : (

          <>

            <SummaryRow label="Razón social" value={companyName || '-'} />

            <SummaryRow label="Tipo de operación" value={operationType || '-'} />

            <SummaryRow

              label="Fecha de vencimiento actual"

              value={formatOriginalDueDateDisplay(currentDueDate || maxDueDate)}

            />

            {mode === 'loan' && (

              <SummaryRow

                label="Cuotas pendientes alcanzadas"

                value={

                  selectedDate

                    ? `${loanReached ?? 0} de ${totalPendingCount}`

                    : `- de ${totalPendingCount}`

                }

              />

            )}

          </>

        )}



        <Card margin={{ top: '3' }}>

          <DatePicker

            label="Fecha de cancelación anticipada *"

            placeholder="Seleccionar fecha"

            displayFormat="DD/MM/AA"

            defaultValue={selectedDate}

            maxDate={parseMaxDueDate(maxDueDate)}

            withoutElevation

            onChange={(date: string) => {

              setSelectedDate(date || '');

              setError(null);

            }}

          />

        </Card>



        <EarlyCancellationBanner variant="info">{infoText}</EarlyCancellationBanner>



        {error && (

          <Card margin={{ top: '2' }}>

            <P type="3" weight="regular" modifier="regular" withMargin={false} variant="danger">

              {error}

            </P>

          </Card>

        )}

      </Card>

    </Modal>

  );

};



export default EarlyCancellationDateModal;

